import { useRef, type ComponentProps, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { OrbitGlyph } from './Spinner';
import { cn, focusRing } from './cn';
import { useHotkey } from './hotkeys';
import { Kbd } from './Kbd';

/** Button styles, also for links that should look like buttons (`<Button asChild><Link/></Button>`). */
export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap',
    'm-0 rounded-md border font-sans font-bold no-underline transition-colors',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
    '[&_svg]:shrink-0',
    focusRing,
  ],
  {
    variants: {
      variant: {
        /** The one main action of a page, dialog or form. */
        primary: 'border-primary bg-primary text-on-primary shadow-xs hover:border-primary-hover hover:bg-primary-hover',
        /** Everything else that is a button. */
        secondary: 'border-line-strong bg-surface text-ink shadow-xs hover:border-ink-faint hover:bg-subtle',
        /** Low-emphasis actions in toolbars and rows. */
        ghost: 'border-transparent bg-transparent text-ink-muted hover:bg-muted hover:text-ink',
        /** Destructive or money-moving actions. */
        danger: 'border-danger bg-danger text-on-danger shadow-xs hover:border-danger-hover hover:bg-danger-hover',
        /** Destructive, but not the main action (e.g. "End ride" next to others). */
        'danger-outline': 'border-danger-line bg-surface text-danger-ink shadow-xs hover:bg-danger-soft',
        /** Reads as a link but behaves as a button. */
        link: 'h-auto border-transparent bg-transparent px-0 text-accent underline-offset-2 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs pointer-coarse:h-9 [&_svg]:size-3.5',
        md: 'h-8 px-3 text-sm pointer-coarse:h-10 [&_svg]:size-4',
        lg: 'h-10 px-4 text-base [&_svg]:size-4',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'px-0' }],
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner and blocks clicks while an action runs. */
  loading?: boolean;
  /** Leading icon (a lucide icon element). */
  icon?: ReactNode;
  /** Renders the child element (e.g. a router Link) with button styles instead of a <button>. */
  asChild?: boolean;
  /**
   * A single-key shortcut that clicks the button (e.g. "r" for Refund), shown as a key hint and
   * listed in the shortcuts dialog. Ignored while typing or when the button is disabled.
   */
  shortcut?: string;
}

/** The console's button. Defaults to `type="button"`, so it never submits a form by accident. */
export function Button({ variant, size, loading, icon, asChild, shortcut, className, children, disabled, type = 'button', ref, ...props }: ButtonProps) {
  const own = useRef<HTMLButtonElement>(null);
  const classes = cn(buttonVariants({ variant, size }), className);
  const inactive = disabled || loading;
  useHotkey(shortcut, () => own.current?.click(), {
    enabled: !inactive,
    scope: own,
    description: props['aria-label'] ?? (typeof children === 'string' ? children : undefined),
  });
  const refs = (node: HTMLButtonElement | null) => {
    own.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };
  if (asChild) {
    return (
      <Slot ref={refs} className={classes} {...props}>
        {children}
      </Slot>
    );
  }
  return (
    <button ref={refs} type={type} className={classes} disabled={inactive} aria-busy={loading || undefined} aria-keyshortcuts={shortcut} {...props}>
      {loading ? <OrbitGlyph /> : icon}
      {children}
      {shortcut && <Kbd keys={shortcut} inverted={variant === 'primary' || variant === 'danger'} className="-mr-1 ml-1" />}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'asChild'> {
  /** Accessible name (also the tooltip-style title). Required: an icon alone says nothing. */
  label: string;
  icon: ReactNode;
}

/** A square button holding only an icon; `label` names it for screen readers and on hover. */
export function IconButton({ label, icon, variant = 'ghost', size = 'md', className, ...props }: IconButtonProps) {
  // touch screens get bigger targets (Button's heights grow the same way)
  const square = size === 'sm' ? 'w-7 px-0 pointer-coarse:w-9' : size === 'lg' ? 'w-10 px-0' : 'w-8 px-0 pointer-coarse:w-10';
  return <Button variant={variant} size={size} aria-label={label} title={label} icon={icon} className={cn(square, className)} {...props} />;
}
